import React from "react";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { formatRupiah } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { Check, Clock, X as XIcon } from "lucide-react";

const STAGES = [
  { key: "pending", label: "Order Dibuat" },
  { key: "paid", label: "Pembayaran Diterima" },
  { key: "processing", label: "Diproses" },
  { key: "done", label: "Selesai" },
];

function Tracker({ status }) {
  const isFailed = status === "failed" || status === "cancelled";
  const currentIdx = isFailed ? -1 : STAGES.findIndex((s) => s.key === status);
  return (
    <div className="border border-black bg-white p-4" data-testid="order-tracker">
      <div className="label-mono text-zinc-500 mb-3">// TRACKING</div>
      <div className="flex items-center justify-between">
        {STAGES.map((s, i) => {
          const done = !isFailed && i <= currentIdx;
          const active = !isFailed && i === currentIdx;
          return (
            <div key={s.key} className="flex-1 flex flex-col items-center text-center">
              <div className={`w-8 h-8 grid place-items-center border border-black ${done ? "bg-[#00C853] text-white" : active ? "bg-[#FFD600]" : "bg-white text-zinc-400"}`}>
                {done ? <Check className="w-4 h-4" /> : active ? <Clock className="w-4 h-4 animate-blink" /> : <span className="font-mono text-xs">{i + 1}</span>}
              </div>
              <span className={`font-mono text-[9px] uppercase tracking-widest mt-2 ${done || active ? "text-black font-bold" : "text-zinc-400"}`}>{s.label}</span>
              {i < STAGES.length - 1 && <div className="hidden" />}
            </div>
          );
        })}
      </div>
      {isFailed && (
        <div className="mt-3 border border-black bg-[#FF3333] text-white p-3 font-mono text-xs flex items-center gap-2">
          <XIcon className="w-4 h-4" /> Order {status === "failed" ? "GAGAL diproses" : "DIBATALKAN"}.
          {status === "failed" && " Saldo otomatis dikembalikan jika sudah dibayar."}
        </div>
      )}
    </div>
  );
}

export default function OrderDetail() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);

  useEffect(() => {
    const fetch = () => api.get(`/orders/${id}`).then((r) => setOrder(r.data)).catch(() => setOrder(false));
    fetch();
    const t = setInterval(fetch, 4000);
    return () => clearInterval(t);
  }, [id]);

  if (order === null) return <div className="p-12 font-mono">// LOADING</div>;
  if (order === false) return <div className="p-12 font-mono">// ORDER NOT FOUND</div>;

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-8 py-10" data-testid="order-detail">
      <Link to="/orders" className="label-mono text-zinc-500 hover:text-black">← Kembali</Link>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="label-mono text-zinc-500">// ORDER</div>
          <h1 className="font-display text-3xl md:text-4xl font-black uppercase mt-1">{order.order_no}</h1>
        </div>
        <StatusBadge status={order.status} large />
      </div>

      <div className="mt-6"><Tracker status={order.status} /></div>

      <div className="mt-6 grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2 border border-black bg-white">
          <div className="p-4 border-b border-black label-mono">// ITEMS</div>
          {order.items.map((item, i) => (
            <div key={i} className="p-4 border-b border-zinc-200 flex gap-3">
              <div className="w-16 h-16 border border-black bg-zinc-100 overflow-hidden">
                {item.image_url && <img src={item.image_url} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1">
                <div className="badge-status bg-white text-[10px]">{item.type.toUpperCase()}</div>
                <div className="font-display font-bold uppercase mt-1">{item.name}</div>
                <div className="font-mono text-xs text-zinc-600 mt-1">{formatRupiah(item.price)} × {item.quantity}</div>
              </div>
              <div className="font-mono font-bold">{formatRupiah(item.subtotal)}</div>
            </div>
          ))}
          <div className="p-4 flex justify-between items-center bg-zinc-100 border-t border-black">
            <span className="label-mono">Total</span>
            <span className="font-mono text-xl font-black">{formatRupiah(order.total)}</span>
          </div>
        </div>

        <aside className="border border-black bg-white p-5 h-fit">
          <div className="label-mono text-zinc-500">// INFO</div>
          <dl className="mt-3 space-y-3 text-sm">
            <div>
              <dt className="label-mono text-zinc-500">Pembayaran</dt>
              <dd className="font-mono mt-1">{order.payment_method === "balance" ? "Saldo Deposit" : "Transfer Manual"}</dd>
            </div>
            {order.shipping_address && (
              <div>
                <dt className="label-mono text-zinc-500">Alamat</dt>
                <dd className="text-xs mt-1">{order.shipping_address}</dd>
              </div>
            )}
            {order.notes && (
              <div>
                <dt className="label-mono text-zinc-500">Catatan</dt>
                <dd className="text-xs mt-1">{order.notes}</dd>
              </div>
            )}
            <div>
              <dt className="label-mono text-zinc-500">Tanggal</dt>
              <dd className="font-mono text-xs mt-1">{new Date(order.created_at).toLocaleString("en-US")}</dd>
            </div>
          </dl>
          {order.payment_method === "manual_transfer" && order.status === "pending" && (
            <div className="mt-4 border border-black bg-[#FFD600] p-3 text-xs font-mono">
              Hubungi admin untuk info pembayaran (Wise / PayPal / Bank Transfer), lalu konfirmasi setelah transfer.
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

